import { Cart } from '../types';
import { useCartState } from './CartProvider';
import addItemRequest from './addItem';
import removeItemRequest from './removeItem';
import updateItemRequest, { CartItemChanges } from './updateItem';
import getCartRequest, { GetCartOptions } from './getCart';

export type UseCart = {
  cart: Cart | null;
  cartCount: number;
  loaded: boolean;
  /**
   * Adds a sku to the cart and answers the updated cart, having already written it
   * back into the provider. Rejects when the endpoint does; the caller decides what
   * that looks like.
   */
  addItem: (sku: string, offerCode?: string | null, paymentPayload?: string | null) => Promise<Cart>;
  /** Removes one line, by the line's own id. */
  removeItem: (itemId: number) => Promise<Cart>;
  /** Changes one line — its quantity, or whether it is a gift. */
  updateItem: (itemId: number, changes: CartItemChanges) => Promise<Cart>;
  /**
   * Re-reads the whole cart, lines included.
   *
   * Needed by anything that draws the lines: what the provider holds on a page that
   * carried `#dm-v2-cart` is the summary, which has none. This is the same request the
   * provider would have made had the page carried nothing.
   *
   * `includeLightboxDeal` additionally prices the upgrade the cart qualifies for. It is
   * a flag on this rather than a request of its own so that the deal and the lines it
   * refers to can only ever come from the same read of the cart.
   */
  refresh: (options?: GetCartOptions) => Promise<Cart>;
};

/**
 * The visitor's cart, and the operations on it.
 *
 * Treat the state as asynchronous even though it usually is not. On a page carrying the
 * payload it resolves the render after mount; on one that is not, it waits for the api.
 * `loaded` is what separates "not yet" from "there is no cart".
 *
 * The operations live beside it rather than in hooks of their own, which is the shape
 * headless settled on: every one of them acts on the cart and answers a new one, so a
 * caller that reads the cart and a caller that changes it should not be looking at two
 * different sources. Each operation is its own file in this folder.
 */
export default function useCart(): UseCart {
  const { cart, loaded, setCart } = useCartState();

  // Every one of these answers the whole cart, so writing it back is what keeps the
  // header's badge and a drawer's lines in step off a single request.
  const write = async (operation: Promise<Cart>): Promise<Cart> => {
    const updated = await operation;

    setCart(updated);

    return updated;
  };

  return {
    cart,
    loaded,
    cartCount: cart?.items_quantity ?? 0,
    addItem: (sku, offerCode, paymentPayload) => write(addItemRequest(sku, offerCode, paymentPayload)),
    removeItem: (itemId) => write(removeItemRequest(itemId)),
    updateItem: (itemId, changes) => write(updateItemRequest(itemId, changes)),
    refresh: (options) => write(getCartRequest(options)),
  };
}
